import React, { useState } from "react";
import { SpaceBetween, Box, Select, Toggle } from "@cloudscape-design/components";
import { JsonViewer, DiffViewer } from "../common";
import { JSONObject } from "../../services";
import { DataCleaner } from "../../utils";
import { LABELS } from "../../constants";

interface HistoryViewProps {
  current: JSONObject | null;
  history: JSONObject[];
}

export const HistoryView: React.FC<HistoryViewProps> = ({ current, history }) => {
  const [selectedVersion, setSelectedVersion] = useState<number>(0);
  const [showDiff, setShowDiff] = useState(true);

  if (history.length === 0) {
    return (
      <Box textAlign="center" color="text-status-inactive" padding="l">
        {LABELS.NO_VERSION_HISTORY}
      </Box>
    );
  }

  const versions = [
    { label: LABELS.CURRENT, value: "current" },
    ...history.map((_, i) => ({
      label: `${LABELS.VERSION} ${history.length - i}`,
      value: String(i),
    })),
  ];

  const cleanData = (data: JSONObject | null) => DataCleaner.removeHistoryMetadata(data);

  const compareFrom = cleanData(selectedVersion === 0 ? history[0] : history[selectedVersion]);
  const compareTo = cleanData(selectedVersion === 0 ? current : history[selectedVersion - 1]);

  const getVersionData = (): JSONObject | null => cleanData(selectedVersion === 0 ? current : history[selectedVersion - 1] || null);

  const getOldTitle = (): string =>
    selectedVersion === 0 ? `${LABELS.VERSION} ${history.length}` : `${LABELS.VERSION} ${history.length - selectedVersion}`;

  const getNewTitle = (): string => (selectedVersion === 0 ? LABELS.CURRENT : `${LABELS.VERSION} ${history.length - selectedVersion + 1}`);

  return (
    <SpaceBetween size="m">
      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <div style={{ flex: "0 0 200px" }}>
          <Select
            selectedOption={versions[selectedVersion]}
            options={versions}
            onChange={({ detail }) => setSelectedVersion(versions.findIndex((v) => v.value === detail.selectedOption.value))}
            placeholder={LABELS.SELECT_VERSION}
          />
        </div>
        <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <Toggle checked={showDiff} onChange={({ detail }) => setShowDiff(detail.checked)}>
            {LABELS.SHOW_DIFF_VIEW}
          </Toggle>
        </div>
        <div style={{ minWidth: "120px", textAlign: "right" }}>
          <Box color="text-status-inactive" fontSize="body-s">
            {history.length} {history.length !== 1 ? "versions" : "version"} {LABELS.IN_HISTORY}
          </Box>
        </div>
      </div>

      {showDiff && compareFrom && compareTo ? (
        <DiffViewer oldData={compareFrom} newData={compareTo} oldTitle={getOldTitle()} newTitle={getNewTitle()} />
      ) : (
        <JsonViewer data={getVersionData()} />
      )}
    </SpaceBetween>
  );
};
